import { Helmet } from "react-helmet-async";
import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { Card, Stack, Container, Typography, Alert } from "@mui/material";
import { get } from "src/utils/networkUtils";
import VersusSeasonsTable from "src/components/versus/VersusSeasonsTable";

export default function VersusSeasonsPage() {
  const [searchParams] = useSearchParams();
  const seasonTypeId = searchParams.get("seasonTypeId");
  const [seasons, setSeasons] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [seasonTypesLoading, setSeasonTypesLoading] = useState(true);
  const [seasonTypeTitle, setSeasonTypeTitle] = useState("");

  const limit = 10;

  useEffect(() => {
    const fetchSeasonTypes = async () => {
      try {
        setSeasonTypesLoading(true);
        const response = await get("/admin/versus/season-types");

        if (seasonTypeId && response) {
          const currentSeasonType = response.versusSeasonTypes.find(
            (type) => type.versusSeasonTypeId === parseInt(seasonTypeId)
          );
          setSeasonTypeTitle(
            currentSeasonType
              ? currentSeasonType.title
              : `Season Type ${seasonTypeId}`
          );
        }
      } catch (error) {
        console.error("Error fetching season types:", error);
        setSeasonTypeTitle(`Season Type ${seasonTypeId}`);
      } finally {
        setSeasonTypesLoading(false);
      }
    };

    fetchSeasonTypes();
  }, [seasonTypeId]);

  const fetchSeasons = useCallback(
    async (currentOffset = 0, isInitial = false) => {
      if (!seasonTypeId) {
        setError("Season Type ID is required");
        return;
      }

      setLoading((prevLoading) => {
        if (prevLoading) return prevLoading;

        (async () => {
          try {
            setError(null);
            const response = await get(
              `/admin/versus/seasons?seasonTypeId=${seasonTypeId}&offset=${currentOffset}&limit=${limit}`
            );
            const newSeasons = response.seasons || [];

            if (isInitial) {
              setSeasons(newSeasons);
            } else {
              setSeasons((prev) => [...prev, ...newSeasons]);
            }

            setHasMore(newSeasons.length === limit);
            setOffset(currentOffset + limit);
          } catch (err) {
            console.error("Error fetching versus seasons:", err);
            setError(err.response?.data?.message || "Failed to fetch seasons");
            if (isInitial) {
              setSeasons([]);
            }
          } finally {
            setLoading(false);
          }
        })();

        return true;
      });
    },
    [seasonTypeId, limit]
  );

  const handleLoadMore = useCallback(() => {
    if (!hasMore) return;
    fetchSeasons(offset);
  }, [hasMore, fetchSeasons, offset]);

  useEffect(() => {
    if (seasonTypeId) {
      setSeasons([]);
      setOffset(0);
      setHasMore(true);
      fetchSeasons(0, true);
    }
  }, [seasonTypeId, fetchSeasons]);

  const handleRefresh = () => {
    setSeasons([]);
    setOffset(0);
    setHasMore(true);
    fetchSeasons(0, true);
  };

  if (!seasonTypeId) {
    return (
      <Container>
        <Alert severity="error">
          Season Type ID is required. Please provide seasonTypeId as a query
          parameter.
        </Alert>
      </Container>
    );
  }

  return (
    <>
      <Helmet>
        <title>Versus Seasons | NHL Ultimate Fan</title>
      </Helmet>

      <Container>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          mb={5}
        >
          <Typography variant="h4" gutterBottom>
            {seasonTypesLoading ? "" : `Versus Seasons ${seasonTypeTitle}`}
          </Typography>
        </Stack>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        <Card>
          <VersusSeasonsTable
            rows={seasons}
            loading={loading}
            hasMore={hasMore}
            onLoadMore={handleLoadMore}
            onRefresh={handleRefresh}
          />
        </Card>
      </Container>
    </>
  );
}
